import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'react-hot-toast';
import { useTheme } from './hooks/useTheme';

import DashboardLayout from './components/layout/DashboardLayout';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import LeadDatabase from './pages/LeadDatabase';
import LeadDetails from './pages/LeadDetails';
import LeadMining from './pages/LeadMining';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import Proposals from './pages/Proposals';
import LandingPage from './pages/LandingPage';
import PermitIntelligence from './pages/PermitIntelligence';
import RoofAgeTrigger from './pages/RoofAgeTrigger';
import LeadMap from './pages/LeadMap';
import AlertEngine from './pages/AlertEngine';
import AutoPilot from './pages/AutoPilot';
import SdrAutopilot from './pages/SdrAutopilot';
import CloseCopilot from './pages/CloseCopilot';
import TitleCheck from './pages/TitleCheck';
import CompanyProfile from './pages/CompanyProfile';
import CreditStore from './pages/CreditStore';
import PaymentSuccess from './pages/PaymentSuccess';
import Leaderboard from './pages/Leaderboard';
import ProposalBuilder from './pages/ProposalBuilder';
import CustomerPortal from './pages/CustomerPortal';
import Webhooks from './pages/Webhooks';
import Referrals from './pages/Referrals';
import Templates from './pages/Templates';
import Campaigns from './pages/Campaigns';
import Appointments from './pages/Appointments';
import LeadScoring from './pages/LeadScoring';
import EnrichmentLab from './pages/EnrichmentLab';
import SkipTrace from './pages/SkipTrace';
import Contractors from './pages/Contractors';
import { AdminRoute } from './components/AdminRoute';
import InvestorProveIt from './pages/InvestorProveIt';
import HiringCenter from './pages/HiringCenter';
import InviteClaim from './pages/InviteClaim';
import Careers from './pages/Careers';
import JobApply from './pages/JobApply';
import ProposalShowcase from './pages/ProposalShowcase';
import RoofDesigner from './pages/RoofDesigner';
import InstallPipeline from './pages/InstallPipeline';
import StormCenter from './pages/StormCenter';
import UtilityRateExplorer from './pages/UtilityRateExplorer';
import CreditCenter from './pages/CreditCenter';
import VoiceCloser from './pages/VoiceCloser';
import TurfMode from './pages/TurfMode';
import PushCenter from './pages/PushCenter';
import VideoEnhance from './pages/VideoEnhance';
import AdShowcase from './pages/AdShowcase';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);
const ThemeContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function useThemeContext() {
  return useContext(ThemeContext);
}

export const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('sls_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('sls_token');
      localStorage.removeItem('sls_user');
      window.location.href = '/auth';
    }
    return Promise.reject(err);
  }
);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('sls_token');
    const savedUser = localStorage.getItem('sls_user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      api.get('/api/auth/me').then(res => {
        setUser(res.data.user);
        localStorage.setItem('sls_user', JSON.stringify(res.data.user));
      }).catch(() => {
        localStorage.removeItem('sls_token');
        localStorage.removeItem('sls_user');
        setUser(null);
      });
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/api/auth/login', { email, password });
    localStorage.setItem('sls_token', res.data.token);
    localStorage.setItem('sls_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  }, []);

  const register = useCallback(async (data) => {
    const res = await api.post('/api/auth/register', data);
    localStorage.setItem('sls_token', res.data.token);
    localStorage.setItem('sls_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('sls_token');
    localStorage.removeItem('sls_user');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-background"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;
  const hasToken = localStorage.getItem('sls_token');
  const hasUser = localStorage.getItem('sls_user');
  if (!user && !hasToken && !hasUser) return <Navigate to="/landing" replace />;
  return children;
}

export default function App() {
  const themeHook = useTheme();

  return (
    <ThemeContext.Provider value={themeHook}>
      <BrowserRouter>
        <AuthProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: themeHook.isDark ? '#1A1F2B' : '#18181B',
                color: '#E4E4E7',
                border: `1px solid ${themeHook.isDark ? '#252B37' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '12px',
              }
            }}
          />
          <Routes>
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/ad" element={<AdShowcase />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/portal/:token" element={<CustomerPortal />} />
            <Route path="/p/:token" element={<ProposalShowcase mode="public" />} />
            <Route path="/invite/:token" element={<InviteClaim />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:slug" element={<JobApply />} />
            <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="leads" element={<LeadDatabase />} />
              <Route path="leads/:id" element={<LeadDetails />} />
              <Route path="mining" element={<LeadMining />} />
              <Route path="scanner" element={<PermitIntelligence />} />
              <Route path="roof-age" element={<RoofAgeTrigger />} />
              <Route path="map" element={<LeadMap />} />
              <Route path="alerts" element={<AlertEngine />} />
              <Route path="autopilot" element={<AutoPilot />} />
              <Route path="sdr" element={<SdrAutopilot />} />
              <Route path="closer" element={<CloseCopilot />} />
              <Route path="voice" element={<VoiceCloser />} />
              <Route path="turf" element={<TurfMode />} />
              <Route path="push" element={<PushCenter />} />
              <Route path="title" element={<TitleCheck />} />
              <Route path="company" element={<CompanyProfile />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="proposals" element={<Proposals />} />
              <Route path="proposals/:id/builder" element={<ProposalBuilder />} />
              <Route path="proposals/:id/view" element={<ProposalShowcase mode="internal" />} />
              <Route path="proposals/:id/roof" element={<RoofDesigner />} />
              <Route path="projects" element={<InstallPipeline />} />
              <Route path="storms" element={<StormCenter />} />
              <Route path="rates" element={<UtilityRateExplorer />} />
              <Route path="video" element={<VideoEnhance />} />
              <Route path="credit" element={<CreditCenter />} />
              <Route path="credits" element={<CreditStore />} />
              <Route path="credits/success" element={<PaymentSuccess />} />
              <Route path="leaderboard" element={<Leaderboard />} />
              <Route path="webhooks" element={<AdminRoute><Webhooks /></AdminRoute>} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="templates" element={<Templates />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="scoring" element={<LeadScoring />} />
              <Route path="enrichment-lab" element={<EnrichmentLab />} />
              <Route path="skip-trace" element={<SkipTrace />} />
              <Route path="contractors" element={<Contractors />} />
              <Route path="prove-it" element={<AdminRoute><InvestorProveIt /></AdminRoute>} />
              <Route path="hiring" element={<AdminRoute><HiringCenter /></AdminRoute>} />
              <Route path="settings" element={<Settings />} />
              <Route path="admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeContext.Provider>
  );
}
